import {
  getContainerHeight,
  getContainerOffset,
  getContainerWidth
} from "@/Utils/getContainerSize";
import { useState, useCallback, useEffect, useRef } from "react";

const useResizable = (initialWidth, initialHeight, minWidth, minHeight) => {
  const [size, setSize] = useState({ width: initialWidth, height: initialHeight });
  const [isResizing, setIsResizing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [startSize, setStartSize] = useState({ width: initialWidth, height: initialHeight });
  const [hasBeenManuallyResized, setHasBeenManuallyResized] = useState(false);
  const containerSizeRef = useRef({ width: 0, height: 0, left: 0, top: 0 });

  useEffect(() => {
    // Only update size if it hasn't been manually resized
    if (!hasBeenManuallyResized) {
      setSize({ width: initialWidth, height: initialHeight });
    }
  }, [initialWidth, initialHeight, hasBeenManuallyResized]);

  const handleMouseDown = useCallback(
    e => {
      e.preventDefault();
      setIsResizing(true);
      setStartPos({ x: e.clientX, y: e.clientY });
      setStartSize(size);
      const containerOffset = getContainerOffset();
      containerSizeRef.current = {
        width: getContainerWidth(),
        height: getContainerHeight(),
        left: containerOffset.left,
        top: containerOffset.top
      };
    },
    [size]
  );

  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      setHasBeenManuallyResized(true);
    }
    setIsResizing(false);
  }, [isResizing]);

  const handleMouseMove = useCallback(
    e => {
      if (isResizing) {
        const dx = e.clientX - startPos.x;
        const dy = e.clientY - startPos.y;

        let newWidth = startSize.width + dx;
        let newHeight = startSize.height + dy;

        const startXInContainer = startPos.x - containerSizeRef.current.left;
        const startYInContainer = startPos.y - containerSizeRef.current.top;

        const maxWidth = containerSizeRef.current.width - startXInContainer + startSize.width;
        const maxHeight = containerSizeRef.current.height - startYInContainer + startSize.height;

        if (newWidth < minWidth) newWidth = minWidth;
        if (newHeight < minHeight) newHeight = minHeight;
        if (newWidth > maxWidth) newWidth = maxWidth;
        if (newHeight > maxHeight) newHeight = maxHeight;

        setSize({ width: newWidth, height: newHeight });
      }
    },
    [isResizing, startPos, startSize, minWidth, minHeight]
  );

  useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return {
    size,
    handleMouseDown,
    handleMouseUp,
    handleMouseMove
  };
};

export default useResizable;
